import {
  ATTACHMENT_ROUTES,
  Biblioteca,
  BibliotecaTelemetria,
  DespachoConocimiento,
  DespachoEpisodes,
  DespachoEpisodeTurns,
  CuadernoFichas,
  CuadernoResumen,
  Ficha,
  LIBRARY_ROUTES,
  VistaPrevia,
  type DuenoRequest,
  type FirmaRequest,
  CatalogoSimulador,
  ConceptosRespuesta,
  CurrentConversation,
  ConfirmSelectedOutcome,
  DecisionAct,
  DESPACHO_ROUTES,
  DespachoMe,
  DespachoProfile,
  HouseSheet,
  DespachoSources,
  DeviceSessions,
  EjecutarRespuesta,
  EmpresasRespuesta,
  EntornosRespuesta,
  EscenariosSimulador,
  ExplorarRespuesta,
  type ExplorarPeticion,
  EngagementSummaries,
  Enrutado,
  InviteOutcome,
  LinkOutcome,
  Me,
  Memoria,
  OpenOutcome,
  Queue,
  ReadAttachmentOutcome,
  type ReadAttachment,
  RecetaRepo,
  RedeemOutcome,
  SIMULADOR_ROUTES,
  STUDIO_ROUTES,
  SendMessageOutcome,
  SignupOutcome,
  STREAM_EVENTS,
  StreamDelta,
  StreamError,
  StreamProgress,
  SkillDetail,
  SkillsTelemetry,
  StudioSkills,
  Team,
  Turns,
  type ConceptosPeticion,
  type EjecutarPeticion,
  type EmpresasPeticion,
  type EntornosPeticion,
} from "@skein/contracts";
import { z } from "zod";
import { createSseParser } from "./sse.js";

export { createSseParser, type SseParser } from "./sse.js";

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly path: string,
    message?: string,
    /** Código de la API (`session`, `used`, `expired`, `admin`…) — lo que decide la pantalla. */
    public readonly code?: string,
    /** Solo en los 410 del canje: `entry` | `invitation` — la vida que tocaba contar. */
    public readonly purpose?: string,
  ) {
    super(message ?? `${status} en ${path}`);
    this.name = "ApiError";
  }
}

export interface ClientOptions {
  baseUrl: string;
  /** Bearer para cada petición; el cliente no sabe de MSAL ni de cookies. */
  getToken: () => Promise<string>;
  fetch?: typeof fetch | undefined;
}

const postJson = (body: unknown): RequestInit => ({ method: "POST", body: JSON.stringify(body) });

export function createStudioClient(opts: ClientOptions) {
  const base = opts.baseUrl.replace(/\/$/, "");
  const doFetch = opts.fetch ?? fetch;
  async function call<S extends z.ZodTypeAny>(path: string, schema: S, init: RequestInit = {}): Promise<z.infer<S>> {
    const headers = new Headers(init.headers);
    headers.set("Authorization", `Bearer ${await opts.getToken()}`);
    if (init.body && !headers.has("Content-Type")) headers.set("Content-Type", "application/json");
    const res = await doFetch(`${base}${path}`, { ...init, headers });
    if (!res.ok) {
      let message: string | undefined;
      let code: string | undefined;
      // `error` es el código de la API (`invalid-recipe`, `invalid-query`…):
      // con él la pantalla decide junto a qué mando enseña el mensaje
      try { ({ message, error: code } = (await res.json()) as { message?: string; error?: string }); } catch { /* sin cuerpo */ }
      throw new ApiError(res.status, path, message, code);
    }
    return schema.parse(await res.json()) as z.infer<S>;
  }
  return {
    me: () => call(STUDIO_ROUTES.me, Me),
    skills: () => call(STUDIO_ROUTES.skills, StudioSkills),
    skill: (slug: string) => call(STUDIO_ROUTES.skill(slug), SkillDetail),
    /** Telemetría real por método; la ventana por defecto (30 días) la pone la API. */
    skillsTelemetria: (dias?: number) =>
      call(dias === undefined ? STUDIO_ROUTES.skillsTelemetria : `${STUDIO_ROUTES.skillsTelemetria}?dias=${dias}`, SkillsTelemetry),
    enrutado: (frase: string) => call(STUDIO_ROUTES.enrutado(frase), Enrutado),
    engagements: () => call(STUDIO_ROUTES.engagements, EngagementSummaries),
    memoria: (engagementId: string) => call(STUDIO_ROUTES.memoria(engagementId), Memoria),
    episodio: (engagementId: string, conversationId: string) => call(STUDIO_ROUTES.episodio(engagementId, conversationId), Turns),
    /**
     * Simulador de conectores (ADR-15). `tokenFuente` es el token delegado
     * de la fuente (Business Central): viaja en el cuerpo de cada petición
     * y la API no lo guarda ni lo devuelve. Una receta que no existe en el
     * repo llega como ApiError 404; un YAML inválido o una consulta mal
     * formada, como ApiError 400 con el mensaje del parser.
     */
    simulador: {
      catalogo: () => call(SIMULADOR_ROUTES.catalogo, CatalogoSimulador),
      escenarios: () => call(SIMULADOR_ROUTES.escenarios, EscenariosSimulador),
      receta: (tipo: string, concepto: string) => call(SIMULADOR_ROUTES.receta(tipo, concepto), RecetaRepo),
      entornos: (input: EntornosPeticion) => call(SIMULADOR_ROUTES.entornos, EntornosRespuesta, postJson(input)),
      empresas: (input: EmpresasPeticion) => call(SIMULADOR_ROUTES.empresas, EmpresasRespuesta, postJson(input)),
      conceptos: (input: ConceptosPeticion) => call(SIMULADOR_ROUTES.conceptos, ConceptosRespuesta, postJson(input)),
      ejecutar: (input: EjecutarPeticion) => call(SIMULADOR_ROUTES.ejecutar, EjecutarRespuesta, postJson(input)),
      /**
       * El explorador (I-18): una pregunta libre a un agente sobre un Bridge.
       * La dirección y el token del Bridge viajan en el cuerpo y no vuelven.
       * Tarda: hasta dos minutos y medio.
       */
      explorar: (input: ExplorarPeticion) => call(SIMULADOR_ROUTES.explorar, ExplorarRespuesta, postJson(input)),
    },
    /**
     * Contadores de uso sin casa de la biblioteca (ADR-20 pt 4): turnos,
     * «sin ficha», enrutado, por Skill y las más citadas. Fuera del
     * espacio `biblioteca` a propósito: así los dobles de la pantalla de
     * firma no tienen que conocerla.
     */
    telemetriaBiblioteca: () => call(LIBRARY_ROUTES.telemetria, BibliotecaTelemetria),
    /**
     * Biblioteca (ADR-14): leer fichas, firmar y elegir cuadernos. Los
     * errores llegan como ApiError con `code`: 409 `stale` (la ficha cambió:
     * hay que volver a leerla), 403 con el porqué (`not-owner`, `no-owner`,
     * `curator`, `criterio-signer`, `wrong-status`, `role`,
     * `not-professional`, `blocked`, o `forbidden` sin rol de biblioteca),
     * 400 `answers` / `comment` / `reference` / `invalid-preview`.
     */
    biblioteca: {
      lista: () => call(LIBRARY_ROUTES.biblioteca, Biblioteca),
      cuaderno: (code: string) => call(LIBRARY_ROUTES.cuaderno(code), CuadernoFichas),
      ficha: (slug: string) => call(LIBRARY_ROUTES.ficha(slug), Ficha),
      vistaPrevia: (slug: string, answers: string[]) => call(LIBRARY_ROUTES.vistaPrevia(slug), VistaPrevia, postJson({ answers })),
      /** Devuelve la ficha ya con la firma nueva. */
      firmar: (slug: string, body: FirmaRequest) => call(LIBRARY_ROUTES.firmas(slug), Ficha, postJson(body)),
      dueno: (code: string, body: DuenoRequest) => call(LIBRARY_ROUTES.dueno(code), CuadernoResumen, postJson(body)),
      /** Cierra un «así no» (Skein Team). Devuelve la ficha. */
      resolver: (signoffId: string, note: string) => call(LIBRARY_ROUTES.resolucion(signoffId), Ficha, postJson({ note })),
    },
  };
}
export type StudioClient = ReturnType<typeof createStudioClient>;

// ── Despacho (ADR-13): el canal del directivo ─────────────────────────
// Su única credencial es el token de sesión que emite la API al canjear
// el enlace. Las rutas de acceso son anónimas POR DISEÑO: nunca mandan
// Authorization, ni aunque haya una sesión vieja en el aparato.

/** Respuesta de los actos sin cuerpo propio. */
const Ok = z.object({ ok: z.boolean() });

export interface DespachoClientOptions {
  baseUrl: string;
  /** El token de sesión, o null si aún no hay ninguna. Síncrono: sale de localStorage. */
  getToken: () => string | null;
  fetch?: typeof fetch | undefined;
}

/** Un mensaje al asesor: texto, un documento ya leído (`readAttachment`) o las dos cosas. */
export interface OutgoingMessage {
  conversationId?: string;
  text: string;
  attachment?: ReadAttachment;
}

export function createDespachoClient(opts: DespachoClientOptions) {
  const base = opts.baseUrl.replace(/\/$/, "");
  const doFetch = opts.fetch ?? fetch;

  /** La petición con sesión (salvo `anon`); lo que no sea 2xx sale como ApiError. */
  async function request(path: string, init: RequestInit & { anon?: boolean } = {}): Promise<Response> {
    const { anon, ...rest } = init;
    const headers = new Headers(rest.headers);
    const token = anon ? null : opts.getToken();
    if (token) headers.set("Authorization", `Bearer ${token}`);
    if (rest.body && !headers.has("Content-Type")) headers.set("Content-Type", "application/json");
    const res = await doFetch(`${base}${path}`, { ...rest, headers });
    if (!res.ok) {
      let message: string | undefined;
      let code: string | undefined;
      let purpose: string | undefined;
      try {
        const body = (await res.json()) as { message?: string; code?: string; purpose?: string };
        message = body.message;
        code = body.code;
        purpose = body.purpose;
      } catch {
        /* sin cuerpo */
      }
      throw new ApiError(res.status, path, message, code, purpose);
    }
    return res;
  }

  async function call<S extends z.ZodTypeAny>(
    path: string,
    schema: S,
    init: RequestInit & { anon?: boolean } = {},
  ): Promise<z.infer<S>> {
    const res = await request(path, init);
    return schema.parse(await res.json()) as z.infer<S>;
  }

  /**
   * La conversación en streaming: `onDelta` recibe cada trozo del texto
   * visible según llega; la promesa resuelve con el MISMO objeto que
   * `send`. Su `reply` manda: quien pinta sustituye lo acumulado por él.
   * `onProgress` (opcional): en una casa con cifras, qué está haciendo el
   * asesor mientras consulta («Consultando ventas…»); el texto llega
   * después, entero.
   */
  async function sendStream(
    input: OutgoingMessage,
    onDelta: (text: string) => void,
    onProgress?: (label: string) => void,
  ): Promise<SendMessageOutcome> {
    const path = DESPACHO_ROUTES.messagesStream;
    const res = await request(path, { ...post(input), headers: { Accept: "text/event-stream" } });
    if (!res.body) throw new ApiError(res.status, path, "La respuesta llegó sin stream.");

    let outcome: SendMessageOutcome | undefined;
    let failure: ApiError | undefined;
    const parser = createSseParser((event, data) => {
      if (outcome || failure) return;
      if (event === STREAM_EVENTS.delta) onDelta(StreamDelta.parse(JSON.parse(data)).text);
      else if (event === STREAM_EVENTS.progress) onProgress?.(StreamProgress.parse(JSON.parse(data)).label);
      else if (event === STREAM_EVENTS.done) outcome = SendMessageOutcome.parse(JSON.parse(data));
      else if (event === STREAM_EVENTS.error) {
        const err = StreamError.parse(JSON.parse(data));
        failure = new ApiError(res.status, path, err.message, err.code);
      }
    });

    const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
    try {
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        parser.push(value);
        if (outcome || failure) break;
      }
      parser.end();
    } finally {
      await reader.cancel().catch(() => {});
    }
    if (failure) throw failure;
    if (!outcome) throw new ApiError(res.status, path, "El stream se cortó antes de terminar el turno.");
    return outcome;
  }

  const post = (body?: unknown, anon = false): RequestInit & { anon?: boolean } => ({
    method: "POST",
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    anon,
  });

  return {
    // acceso (sin sesión)
    link: (email: string) => call(DESPACHO_ROUTES.link, LinkOutcome, post({ email }, true)),
    signup: (input: { email: string; name: string; companyName: string }) =>
      call(DESPACHO_ROUTES.signup, SignupOutcome, post(input, true)),
    redeem: (input: { token: string; device?: string }) => call(DESPACHO_ROUTES.redeem, RedeemOutcome, post(input, true)),
    // con sesión
    me: () => call(DESPACHO_ROUTES.me, DespachoMe),
    logout: () => call(DESPACHO_ROUTES.logout, Ok, post()),
    sessions: () => call(DESPACHO_ROUTES.sessions, DeviceSessions),
    revokeSession: (id: string) => call(DESPACHO_ROUTES.session(id), Ok, { method: "DELETE" }),
    revokeOthers: () => call(DESPACHO_ROUTES.sessions, Ok, { method: "DELETE" }),
    invite: (input: { email: string; name?: string }) => call(DESPACHO_ROUTES.invite, InviteOutcome, post(input)),
    conversation: () => call(DESPACHO_ROUTES.conversation, CurrentConversation),
    /** Primera vez (UC-01): abre el asesor. Con turnos, la API responde 409 `conflict`. */
    open: () => call(DESPACHO_ROUTES.open, OpenOutcome, post()),
    /**
     * Conversación nueva (2026-10-27): el asesor abre otro episodio aunque
     * el de ahora tenga turnos. Nada se borra y la memoria se conserva; la
     * conversación vigente pasa a ser la nueva. Desde ADR-21 es una
     * herramienta de pruebas: solo con `CurrentConversation.testTools`; si
     * no, 404 `test-tools`. Cierra el episodio abierto con su puente.
     */
    newEpisode: () => call(DESPACHO_ROUTES.newEpisode, OpenOutcome, post()),
    /** Mis episodios (ADR-21), del más reciente al más viejo, con estado, fechas y puente. */
    episodes: () => call(DESPACHO_ROUTES.episodes, DespachoEpisodes),
    /** Un episodio mío con sus turnos (otro directivo: 404 `not-found`). */
    episode: (conversationId: string) => call(DESPACHO_ROUTES.episode(conversationId), DespachoEpisodeTurns),
    /** Lo que espera a que quieras (R-3): propuestas no activas y temas del asesor. */
    queue: () => call(DESPACHO_ROUTES.queue, Queue),
    /** La fuente de cifras de mi casa (R-8, ADR-17 pt 7): ninguna, demostración o la cabecera de su foto. */
    sources: () => call(DESPACHO_ROUTES.sources, DespachoSources),
    /**
     * La vista pública de una ficha citada en un Message mío (ADR-20 pt
     * 5): título, cuaderno, clase, resumen, fuentes y la posición de la
     * traza. Una ficha no citada en ese Message, o un Message que no es
     * mío, es 404 `not-found`.
     */
    conocimiento: (slug: string, messageId: string) => call(DESPACHO_ROUTES.conocimiento(slug, messageId), DespachoConocimiento),
    send: (input: OutgoingMessage) =>
      call(DESPACHO_ROUTES.messages, SendMessageOutcome, post(input)),
    sendStream,
    /**
     * Documento → texto (NO se envía al asesor: el texto viaja después en
     * `send`/`sendStream` como `attachment`). Sube el fichero crudo con su
     * nombre; que no se pueda leer vuelve como `rejected` con su causa, y
     * el 413 del tope también (`too-big`), no como excepción.
     */
    readAttachment: async (file: Blob, name: string): Promise<ReadAttachmentOutcome> => {
      const path = `${ATTACHMENT_ROUTES.read}?name=${encodeURIComponent(name)}`;
      try {
        // Un Blob sin tipo saldría sin Content-Type: se fuerza el genérico (la API mira la extensión).
        const res = await request(path, { method: "POST", body: file, headers: { "Content-Type": file.type || "application/octet-stream" } });
        return ReadAttachmentOutcome.parse(await res.json());
      } catch (err) {
        if (err instanceof ApiError && err.status === 413) return { status: "rejected", reason: "too-big" };
        throw err;
      }
    },
    // `statement` SOLO viaja con `correct`: es el enunciado nuevo del criterio.
    factAct: (chainId: string, factId: string, act: "confirm" | "reject" | "retract" | "correct", statement?: string) =>
      call(DESPACHO_ROUTES.factAct(chainId, factId), Ok, post({ act, ...(statement !== undefined ? { statement } : {}) })),
    /**
     * «Confirmar lo marcado» (ADR-21 pt 6): el acto `confirm-selected` sobre
     * varios hechos propuestos. Responde hecho a hecho — `confirmed` o
     * `skipped` con su porqué —: el bloque no es atómico.
     */
    confirmSelected: (facts: ReadonlyArray<{ chainId: string; factId: string }>) =>
      call(DESPACHO_ROUTES.factsConfirmSelected, ConfirmSelectedOutcome, post({ facts: facts.map(({ chainId, factId }) => ({ chainId, factId })) })),
    resolveFollowUp: (id: string) => call(DESPACHO_ROUTES.followUpResolve(id), Ok, post()),
    // «Lo que sé»: el perfil ya gateado por §5 — el cliente no filtra nada.
    profile: () => call(DESPACHO_ROUTES.profile, DespachoProfile),
    /**
     * La ficha de la casa (ADR-21 pt 6): lo confirmado que puedo leer, en
     * prosa y por grupos de «Lo que sé», con su texto (`markdown`) para
     * copiar o descargar. Se genera al vuelo; nunca se edita.
     */
    houseSheet: () => call(DESPACHO_ROUTES.houseSheet, HouseSheet),
    /**
     * Actos sobre una decisión: descartarla o revisarla con acta. Acepta
     * `"drop"` a secas (la firma del tramo 2) y el objeto del tramo 3.
     */
    decisionAct: (id: string, act: "drop" | DecisionAct = "drop") =>
      call(DESPACHO_ROUTES.decisionAct(id), Ok, post(typeof act === "string" ? { act } : act)),
    followUpAct: (id: string, act: "resolve" | "drop") => call(DESPACHO_ROUTES.followUpAct(id), Ok, post({ act })),
    /** §5: el propio nombre, y solo el propio. Devuelve el «yo» ya actualizado. */
    setName: (name: string) => call(DESPACHO_ROUTES.name, DespachoMe, { method: "PATCH", body: JSON.stringify({ name }) }),
    /**
     * Lo estructural de la casa (Client Admin): devuelve la casa con su
     * rastro R-2. Sin cambios de verdad, la API responde 400 `invalid`.
     */
    updateEngagement: (patch: { companyName?: string; sector?: string | null }) =>
      call(DESPACHO_ROUTES.engagement, DespachoProfile.shape.engagement, { method: "PATCH", body: JSON.stringify(patch) }),
    // Cuenta · equipo (Client Admin; a los demás la API responde 403 `admin`).
    team: () => call(DESPACHO_ROUTES.team, Team),
    resendInvitation: (executiveId: string) => call(DESPACHO_ROUTES.teamResend(executiveId), Ok, post()),
    setAdmin: (executiveId: string, grant: boolean) => call(DESPACHO_ROUTES.teamAdmin(executiveId), Ok, post({ grant })),
  };
}
export type DespachoClient = ReturnType<typeof createDespachoClient>;
export * from "./voice";
